/**
 * La géométrie de la shortlist — pure, sans React, sans DOM.
 *
 * Ce module est extrait de `ShortlistStack.tsx` pour une seule raison : le
 * rendu WebGL (§D42) et le rendu DOM doivent poser les cartes **au même
 * endroit**, et la seule façon d'en être sûr est qu'ils appellent le même
 * code. Chaque valeur dupliquée entre les deux moteurs serait une divergence
 * qui attend son heure ; ici il n'y en a aucune. `ShortlistStack.tsx`
 * consomme ce module et le ré-exporte, si bien que le banc d'essai continue
 * de muter LES MÊMES objets `WHEEL`/`OPENING` que lisent les deux moteurs.
 *
 * Ce qui reste dehors : tout ce qui touche à un nœud ou à un composant. Ce
 * qui entre ici : les réglages, les easings, le trajet, le cadrage, et la
 * mesure du défilement.
 *
 * **Une note sur la fidélité.** Les formules sont recopiées telles quelles,
 * artefacts compris — l'implémentation DOM est la spécification exécutable de
 * cette interface, réglée à l'œil par Vincent. Deux divergences apparentes
 * sont donc conservées volontairement et signalées sur place : le cadrage du
 * repos ne divise pas par la même quantité selon qu'on calcule une pose ou la
 * course du conteneur, et la hauteur de carte du conteneur est une
 * approximation. Corriger l'une ou l'autre changerait le rendu.
 */

/** Hauteur de la barre de chrome. Un pouce y tape sans viser. */
export const CHROME_HEIGHT = 34;

/**
 * Largeur de conception d'une carte : la largeur utile d'un iPhone (375 − les
 * marges). Le contenu est TOUJOURS mis en page à cette largeur-là — typo,
 * marges, chrome — puis la carte entière est mise à l'échelle de sa largeur
 * réelle. Un même onglet est donc identique au pixel près sur un téléphone et
 * sur un grand écran, juste plus grand — c'est le comportement d'une
 * miniature d'écran, pas d'une page fluide.
 */
export const BASE_CARD_W = 343;

/**
 * Largeur maximale de la pile : une colonne de téléphone, centrée. Les cartes
 * sont des snapshots pensés portrait — sur une fenêtre large, les étirer à
 * pleine largeur donne des pages de 800 px avec une typo ×2,3 ; on plafonne
 * l'objet plutôt que de le déformer.
 */
export const MAX_STACK_W = 430;

/**
 * Plancher de la colonne utile. Les curseurs du banc poussent la gouttière
 * jusqu'à 40 px par côté ; sur une fenêtre étroite, le produit finirait par
 * manger la carte. Sans effet sur un téléphone réel — le plus étroit garde
 * encore ~215 px à la gouttière maximale.
 */
export const MIN_STACK_W = 200;

/**
 * Les réglages du trajet — un objet mutable plutôt que des constantes, pour
 * que le banc d'essai (§D41) les pilote en direct avec ses curseurs. En
 * production, les défauts font foi.
 *
 * L'état BAS (`bottom*`, plus `frontGap` qui place la lecture) est la pose
 * d'entrée : c'est là qu'on lit. L'état HAUT (`top*`) est la pose de sortie.
 * `step` est le pas de défilement entre deux cartes, `fade` la fraction de
 * trajet sur laquelle une carte s'éteint passé la sortie. `perspective` et
 * `originY` sont la caméra — focale et hauteur d'axe.
 *
 * `startY` cadre le repos : au début du scroll, la pile ne monte pas
 * jusqu'à la sortie — sa carte la plus haute s'arrête à cette fraction de
 * l'écran (0,5 = moitié), et c'est le défilement qui fait continuer les
 * cartes vers la sortie. Demande de Vincent : au repos, la moitié haute de
 * l'écran reste de l'air.
 *
 * Les valeurs par défaut sont le réglage de Vincent (28/08/2026, JSON du
 * banc collé tel quel, troisième itération). Sa signature : lecture en
 * avant de la vitre (+110) et à peine penchée (−11°), axe caméra BAS (500 —
 * la caméra regarde la pile par en dessous du milieu), repos haut perché
 * (startY 0,24), et la plongée en vitesse activée (speedDepth 55 % : le
 * milieu s'enfonce pendant les pichenettes).
 */
export const WHEEL = {
  // ── Point BAS : la lecture (p = 0) ─────────────────────────────────────
  /** Position de lecture : hauteur du CENTRE de la carte, depuis le bas. */
  frontGap: 0,
  bottomTilt: -11,
  bottomZ: 110,
  /**
   * Poids d'espacement au point bas — la densité locale des cartes à
   * l'écran. Seuls les RAPPORTS entre les trois poids comptent : la courbe
   * est normalisée pour que la sortie reste à sa place. Les défauts
   * reproduisent l'ancienne décélération globale (1,7).
   */
  gapBottom: 0.7,

  // ── Point MILIEU : keyframe libre sur le trajet ────────────────────────
  /** Position du point milieu, en fraction de trajet (0 = lecture, 1 = sortie). */
  midAt: 0.5,
  midTilt: -24,
  midZ: 20,
  gapMid: 1.05,
  /**
   * Réaction à la vitesse : en scroll rapide, la profondeur du point milieu
   * se décale de ce pourcentage de l'amplitude du trajet (topZ − bottomZ) —
   * pleine vitesse ≈ une pichenette (3000 px/s). Positif = le milieu plonge,
   * négatif = il vient vers l'œil. 0 = désactivé.
   */
  speedDepth: 55,
  /** Easing bas → milieu : exposant sur le temps local (1 = linéaire,
   *  > 1 démarre lentement, < 1 démarre vite). */
  easeLow: 1,

  // ── Point HAUT : la sortie (p = 1) ─────────────────────────────────────
  /** Hauteur de sortie, en fraction de la hauteur du viewport (négatif = au-dessus). */
  topY: 0.02,
  topTilt: -38,
  topZ: -380,
  gapTop: 0.3,
  /** Easing milieu → haut, même convention. */
  easeHigh: 1,

  // ── Trajet ─────────────────────────────────────────────────────────────
  step: 98,
  /** Au repos, le haut de la pile s'arrête à cette fraction de l'écran. */
  startY: 0.24,
  /**
   * En FIN de liste, la dernière carte peut remonter le trajet jusqu'à ce
   * que son centre atteigne cette fraction de l'écran — sans ça, la butée
   * l'arrêtait à la position de lecture, à moitié coupée quand la lecture
   * est au ras du bas.
   */
  endY: 0.44,
  /** Fraction de trajet sur laquelle une carte s'éteint passé la sortie. */
  fade: 0.3,
  /**
   * Gouttière, en px PAR CÔTÉ : l'air entre la carte et le bord de la
   * colonne. C'est un réglage de VIEWPORT, pas de carte — il fixe la largeur
   * maximale d'une carte (`colonne − 2·gutter`), celle qu'atteignent la carte
   * de lecture et, depuis §D45, la carte ouverte. Le budget s'y adapte tout
   * seul : changer une profondeur au banc change `maxPop`, donc la largeur de
   * base, et la carte la plus poppée retombe toujours exactement là.
   */
  gutter: 12,

  // ── Caméra ─────────────────────────────────────────────────────────────
  perspective: 780,
  originY: 500,
};

/**
 * La chorégraphie d'OUVERTURE — mutable pour le banc, comme `WHEEL`.
 *
 * La séquence de la carte piochée : traction de `pull` px dans son propre
 * plan (l'angle tenu) jusqu'à `pullAt`, où elle est extraite de la pile
 * (passage au premier plan), puis voyage vers le centre. La rotation vers le
 * plat a sa propre fenêtre — `rotateFrom` → `rotateTo`, en fractions de la
 * séquence — pour tenir l'angle pendant la traction et finir de s'aplatir
 * quand on veut. Les autres cartes s'écartent sur leur propre tempo
 * (`spreadDelay`, `spreadDuration`).
 *
 * Les easings sont une famille sigmoïde à un paramètre : 1 = linéaire,
 * 2 ≈ easeInOut, au-dessous de 1 la courbe s'inverse (rapide au milieu).
 */
export const OPENING = {
  /** Durée totale de la séquence, en secondes (le slow-mo la multiplie ×6). */
  duration: 0.4,
  /** Traction, en px le long du plan de la carte. */
  pull: 165,
  /** Fin de la traction ET moment de l'extraction, en fraction de séquence. */
  pullAt: 0.24,
  easePull: 3,
  easeTravel: 2,
  /** Fenêtre de la mise à plat, en fractions de séquence. */
  rotateFrom: 0,
  rotateTo: 1,
  /**
   * L'écartement des autres cartes, par côté : celles AU-DESSUS de la
   * piochée fuient vers le haut, celles EN DESSOUS vers le bas — et le bas
   * part plus vite par défaut : c'est lui qui dégage le chemin de la
   * traction. Chaque côté a son délai et sa durée ; l'easing est commun.
   */
  spreadDelay: 0,
  spreadDuration: 0.3,
  spreadBelowDelay: 0,
  spreadBelowDuration: 0.15,
  /** Distance de poussée à l'écran, plus un cran par rang d'éloignement. */
  spreadDist: 280,
  easeSpread: 2,
};

/**
 * L'APPARITION de la pile — le montage de la scène, **en GL uniquement**
 * (§D46). Mutable pour le banc, comme `WHEEL` et `OPENING`.
 *
 * Chaque carte arrive dans sa pose depuis plus bas sur SON PROPRE trajet :
 * l'avancement est reculé de `distance` px de trajet, puis rattrapé — c'est
 * donc exactement le chemin qu'elle prendrait au défilement, pas une
 * translation plaquée par-dessus. L'opacité monte de 0 à sa valeur sur la même
 * courbe, et `stagger` décale les cartes une à une, la plus BASSE d'abord :
 * l'ordre d'une donne, la carte de lecture posée en premier et le reste qui
 * s'empile vers la sortie.
 *
 * La référence DOM, elle, monte en `initial={false}` et ne bouge pas : la
 * divergence est assumée et documentée (§D46) — cette DA-là atterrit dans le
 * moteur d'avenir.
 */
export const ENTRANCE = {
  /** Durée de l'arrivée d'UNE carte, en secondes. */
  duration: 0.5,
  /** Décalage entre deux cartes voisines, en secondes. */
  stagger: 0.045,
  /** Recul du départ, en px de trajet (converti en avancement par `span`). */
  distance: 120,
};

/**
 * Exposant de l'easing de l'apparition — la même famille sigmoïde que
 * l'ouverture. 2 ≈ easeInOut : la carte démarre et se pose en douceur, sans
 * rebond. Pas de curseur : c'est le caractère du mouvement, pas un réglage.
 */
const ENTRANCE_EASE = sigmoidEase(2);

/**
 * La MATIÈRE des cartes et le décor derrière elles (§D46) — mutable pour le
 * banc, groupe « Matière ».
 *
 * `thickness` est la fausse extrusion : la silhouette redessinée sous la face
 * avant, décalée du vecteur d'épaisseur projeté, en px de conception. Les deux
 * réglages d'ombre agissent sur les trois ombres portées du shader de surface
 * — `shadowOpacity` sur leur alpha, `shadowReach` sur leurs décalages ET leurs
 * flous. `pageBg` est la couleur derrière la pile : le fond du renderer et
 * celui du conteneur de la scène, jamais celui de la coquille de l'app.
 *
 * L'ÉCLAT D'ARÊTE (§D47) est le troisième groupe : un liseré lumineux le long
 * du bord de la silhouette — celui de la face avant, et celui de la tranche,
 * qui est l'arête qui vend le volume. `edgeGlint` en règle la force (0 =
 * éteint), `glintWidth` la largeur de la bande en px de conception, et
 * `lightAngle` la direction d'où vient la lumière, à l'ÉCRAN : convention des
 * dégradés CSS, 0° éclaire par le haut et l'angle tourne dans le sens des
 * aiguilles. Le défaut −35° place la source en haut à gauche, du même côté que
 * le rehaut interne d'un pixel que porte déjà la carte.
 *
 * Le poids du liseré est l'orientation du contour face à la lumière, projetée
 * par l'inclinaison de la carte : l'éclat se déplace donc quand la carte se
 * penche, au lieu d'être peint dessus.
 *
 * **Les quatre premiers défauts sont le réglage de Vincent du 29/08/2026**
 * (JSON du banc collé tel quel) : épaisseur poussée à fond, ombres au maximum
 * d'opacité et longues, et un fond JAUNE ACIDE. Ce fond n'est pas une lubie —
 * c'est le cas qui rend le masquage des ombres (§D47) visible : sur du clair,
 * la moindre ombre égarée sur le décor se verrait de l'autre bout de la pièce.
 * Les trois réglages d'éclat gardent leurs valeurs sobres, Vincent les réglera
 * lui-même.
 */
export const SURFACE = {
  thickness: 8,
  shadowOpacity: 2,
  shadowReach: 2.5,
  pageBg: "#e4ef65",
  edgeGlint: 0.5,
  glintWidth: 1.5,
  lightAngle: -35,
};

/** Famille d'easings sigmoïde : e = 1 linéaire, e = 2 ≈ easeInOut. */
export function sigmoidEase(e: number): (t: number) => number {
  return (t) => {
    const a = Math.pow(t, e);
    return a / (a + Math.pow(1 - t, e));
  };
}

/**
 * Descente d'écran pilotée par la densité : le poids d'espacement de chaque
 * point donne la distance locale entre cartes voisines, la position est
 * l'intégrale (piecewise quadratique, normalisée pour que p = 1 tombe sur la
 * sortie). Sous la lecture (p < 0), la pente du point bas se prolonge — c'est
 * l'escalier d'attente ; passé la sortie (p > 1), on stoppe : le fondu fait
 * le reste.
 */
export function gapIntegral(p: number): number {
  const { midAt, gapBottom: gB, gapMid: gM, gapTop: gT } = WHEEL;
  const A = (midAt * (gB + gM)) / 2;
  const B = ((1 - midAt) * (gM + gT)) / 2;
  const total = Math.max(1e-6, A + B);
  if (p <= 0) return (p * gB) / total;
  if (p <= midAt) return (p * gB + ((gM - gB) * p * p) / (2 * midAt)) / total;
  const q = Math.min(p, 1) - midAt;
  return (A + q * gM + ((gT - gM) * q * q) / (2 * (1 - midAt))) / total;
}

/** Inverse exacte de `gapIntegral` sur [0, 1] — sert au cadrage du repos. */
export function gapInverse(E: number): number {
  const { midAt, gapBottom: gB, gapMid: gM, gapTop: gT } = WHEEL;
  const A = (midAt * (gB + gM)) / 2;
  const B = ((1 - midAt) * (gM + gT)) / 2;
  const total = Math.max(1e-6, A + B);
  const T = Math.min(Math.max(E, 0), 1) * total;

  const solve = (b: number, c2: number, target: number) => {
    if (Math.abs(c2) < 1e-9) return target / Math.max(b, 1e-6);
    const disc = Math.max(0, b * b + 4 * c2 * target);
    return (-b + Math.sqrt(disc)) / (2 * c2);
  };

  if (T <= A) return solve(gB, (gM - gB) / (2 * midAt), T);
  return midAt + solve(gM, (gT - gM) / (2 * (1 - midAt)), T - A);
}

/**
 * Interpolation par keyframes : bas (p = 0) → milieu (p = midAt) → haut
 * (p = 1), avec un easing en exposant par segment. Inclinaison et profondeur
 * passent toutes deux par là — le point milieu est un vrai état
 * intermédiaire, pas juste une inflexion d'angle.
 */
export function keyframed(p: number, bottom: number, mid: number, top: number): number {
  const { midAt, easeLow, easeHigh } = WHEEL;
  if (p <= midAt) {
    const t = midAt <= 0 ? 1 : p / midAt;
    return bottom + (mid - bottom) * Math.pow(t, easeLow);
  }
  const t = midAt >= 1 ? 1 : (p - midAt) / (1 - midAt);
  return mid + (top - mid) * Math.pow(t, easeHigh);
}

/**
 * Ressort unique pour toute la pile — le même à l'ouverture et à la fermeture :
 * un objet physique ne change pas de masse selon le sens du mouvement.
 */
export const STACK_SPRING = {
  type: "spring",
  stiffness: 280,
  damping: 30,
  mass: 0.9,
} as const;

/**
 * Interrupteur d'observation du banc : ralentit l'ouverture/fermeture sans en
 * changer le caractère. Raideur ÷ k² et amortissement ÷ k conservent le taux
 * d'amortissement — c'est EXACTEMENT le même mouvement, joué k fois plus
 * lentement (k = 6).
 */
export const MOTION = { slow: false };

export const SLOW_SPRING = {
  type: "spring",
  stiffness: STACK_SPRING.stiffness / 36,
  damping: STACK_SPRING.damping / 6,
  mass: STACK_SPRING.mass,
} as const;

/**
 * Suivi du défilement : INSTANTANÉ, sans aucun ressort.
 *
 * La `perspective-origin` suit le scroll en style direct, immédiatement. Si
 * les poses la poursuivent à travers un ressort — même très raide — les
 * cartes sont projetées pendant le geste depuis une origine en avance sur
 * elles : un cisaillement transitoire, qui se lit à l'écran comme une légère
 * rotation désaxée et se résorbe à l'arrêt. Origine et poses doivent avancer
 * dans la même frame. La fluidité du geste vient du défilement natif
 * lui-même ; les ressorts restent pour l'ouverture et la fermeture.
 */
export const WHEEL_FOLLOW = { duration: 0 } as const;

/** Durée effective de la séquence d'ouverture (slow-mo compris). */
export function openDuration(): number {
  return OPENING.duration * (MOTION.slow ? 6 : 1);
}

/** Cadence des keyframes de position : traction puis voyage. */
export function openPoseTransition() {
  return {
    duration: openDuration(),
    times: [0, OPENING.pullAt, 1],
    ease: [sigmoidEase(OPENING.easePull), sigmoidEase(OPENING.easeTravel)],
  };
}

/** Cadence de la mise à plat : l'angle tenu jusqu'à `rotateFrom`. */
export function openRotateTransition() {
  return {
    duration: openDuration(),
    times: [0, OPENING.rotateFrom, Math.max(OPENING.rotateTo, OPENING.rotateFrom + 0.01)],
    ease: [sigmoidEase(1), sigmoidEase(OPENING.easeTravel)],
  };
}

/** L'écartement des autres cartes — tempo par côté de la carte piochée. */
export function spreadTransition(below: boolean) {
  const k = MOTION.slow ? 6 : 1;
  return {
    type: "tween" as const,
    delay: (below ? OPENING.spreadBelowDelay : OPENING.spreadDelay) * k,
    duration: (below ? OPENING.spreadBelowDuration : OPENING.spreadDuration) * k,
    ease: sigmoidEase(OPENING.easeSpread),
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Le trajet
// ─────────────────────────────────────────────────────────────────────────

/** L'état du viewport et de la scène dont dépend une frame entière. */
export interface StackFrameInput {
  count: number;
  /** Axe caméra : position du centre du viewport dans le repère de la pile. */
  focus: number;
  /** Largeur d'une carte, DÉJÀ budgétée par `budgetedCardWidth`. */
  width: number;
  /** Vitesse de défilement lissée (px/s) — nourrit `speedDepth`. */
  speed: number;
  /** Position viewport du bord haut de la scène. */
  stageTop: number;
  /** `window.innerHeight`. */
  H: number;
  /** `window.innerWidth` — donne le ratio d'écran de la carte. */
  W: number;
}

/** La pose d'une carte, en pixels CSS de la scène. */
export interface CardPose {
  index: number;
  /** Avancement sur le trajet : 0 = lecture, 1 = sortie, négatif = en attente. */
  p: number;
  /** Hauteur VIEWPORT du centre de la carte sur le trajet. */
  yCenter: number;
  /** Ordonnée de la boîte dans le repère de la scène (bord haut, non scalée). */
  y: number;
  /** Profondeur réelle envoyée à la caméra — négative ou nulle. */
  z3d: number;
  tilt: number;
  /** Grossissement caméra du pop de lecture, cuit en échelle 2D. */
  kPop: number;
  fade: number;
  /** Faux quand la carte est cullée (au repos uniquement). */
  visible: boolean;
}

/** Ce qu'une frame a en commun pour toutes les cartes. */
export interface StackFrame {
  poses: CardPose[];
  /** Hauteur de la boîte non scalée d'une carte — le ratio de l'écran. */
  cardH: number;
  /** Échelle du contenu depuis la largeur de conception iPhone. */
  scale: number;
}

/**
 * Ce qui ne dépend PAS de la carte : la géométrie du viewport, les deux
 * butées, la profondeur du milieu à la vitesse courante. Séparé des poses
 * parce que le DOM rend carte par carte (un composant par onglet) et le GL
 * d'un bloc : le contexte se calcule une fois, la pose se lit ensuite en
 * temps constant dans les deux moteurs.
 */
export interface StackContext {
  cardH: number;
  scale: number;
  stageTop: number;
  H: number;
  readY: number;
  topYpx: number;
  span: number;
  stepPx: number;
  /** Cadrage du repos, en px de trajet déjà consommés au scroll nul. */
  base: number;
  /** Avancement courant du défilement, borné par les deux butées. */
  advance: number;
  /** Profondeur du point milieu corrigée par la vitesse (`speedDepth`). */
  midZLive: number;
  fadeSpan: number;
}

/**
 * Le contexte d'une frame, à partir du seul état mesuré.
 *
 * C'est la traduction littérale du bloc de calcul de `StackItem`, sorti de la
 * boucle de rendu React pour que le moteur GL calcule EXACTEMENT la même
 * chose. Le calcul par carte est trivial ; ce qui l'est moins, ce sont les
 * deux butées — le cadrage du repos (`base`) et celui de la fin de liste
 * (`maxW`) — qui dépendent du nombre de cartes et pas de la carte.
 */
export function stackContext(input: StackFrameInput): StackContext {
  const { count, focus, width, speed, stageTop, H, W } = input;

  /**
   * La carte a le ratio de l'écran : c'est un snapshot de page, comme les
   * onglets de Safari — la suivante recouvre presque tout, la carte de
   * lecture laisse voir son début de page, et l'ouverture montre le reste.
   */
  const cardH = width * (H / W);
  const scale = width / BASE_CARD_W;

  const readY = H - WHEEL.frontGap;
  const topYpx = WHEEL.topY * H;
  const span = Math.max(120, readY - topYpx);
  const stepPx = WHEEL.step;

  /**
   * Cadrage du repos : au début du scroll, la carte la plus haute ne monte
   * pas jusqu'à la sortie — elle s'arrête au niveau `startY` de l'écran
   * (bord haut de carte ≈ centre + un tiers de carte). On inverse la courbe
   * du trajet pour convertir cette hauteur en avancement maximal, et `base`
   * s'y plafonne. Le défilement, lui, continue jusqu'à la sortie.
   *
   * Le diviseur est `readY − topYpx` et NON `span` : les deux ne diffèrent
   * que sur un viewport si court que le trajet passe sous le plancher de
   * 120 px, et l'écart est déjà dans le rendu de référence. On le garde.
   */
  const yTarget = WHEEL.startY * H + cardH / 2;
  const easedCap = Math.min(Math.max((readY - yTarget) / (readY - topYpx), 0), 1);
  const pCap = gapInverse(easedCap);
  /**
   * Cadrage de la fin, symétrique du repos : la course se prolonge de `pEnd`
   * pour que la DERNIÈRE carte puisse remonter jusqu'à la ligne `endY`
   * (centre à la moitié de l'écran par défaut) au lieu de buter, à moitié
   * coupée, sur la position de lecture.
   */
  const easedEnd = Math.min(Math.max((readY - WHEEL.endY * H) / (readY - topYpx), 0), 1);
  const pEnd = gapInverse(easedEnd);
  const base = Math.min((count - 1) * stepPx, pCap * span);
  const maxW = Math.max(0, (count - 1) * stepPx - base + pEnd * span);
  const advance = Math.min(Math.max(focus - H / 2, 0), maxW);

  // En vitesse, le point milieu se décale en profondeur — pourcentage de
  // l'amplitude du trajet, pleine vitesse à 3000 px/s (une pichenette).
  const vNorm = Math.min(Math.abs(speed) / 3000, 1);
  const midZLive =
    WHEEL.midZ + vNorm * (WHEEL.speedDepth / 100) * (WHEEL.topZ - WHEEL.bottomZ);

  return {
    cardH,
    scale,
    stageTop,
    H,
    readY,
    topYpx,
    span,
    stepPx,
    base,
    advance,
    midZLive,
    fadeSpan: WHEEL.fade,
  };
}

/**
 * La pose d'une carte.
 *
 * `p` est l'avancement de la carte sur son trajet : 0 à la position de
 * lecture (état bas), 1 à la sortie (état haut), négatif en dessous de la
 * lecture — la carte glisse alors depuis le bord bas dans sa pose d'entrée.
 */
export function poseAt(ctx: StackContext, index: number): CardPose {
  const { cardH, stageTop, H, readY, topYpx, span, stepPx, base, advance, midZLive, fadeSpan } =
    ctx;

  const p = (advance + base - index * stepPx) / span;
  const eased = gapIntegral(p);
  const clamped = Math.min(Math.max(p, 0), 1);

  const yCenter = readY - (readY - topYpx) * eased;
  const tilt = keyframed(clamped, WHEEL.bottomTilt, WHEEL.midTilt, WHEEL.topTilt);
  const z = keyframed(clamped, WHEEL.bottomZ, midZLive, WHEEL.topZ);
  /**
   * Les profondeurs POSITIVES (le pop de lecture) ne partent pas au GPU en
   * `translateZ` : Safari rastérise le contenu à sa taille de mise en page
   * puis laisse la caméra l'agrandir — texte flou, le sur-échantillonnage
   * n'y suffisait pas. On cuit l'agrandissement en échelle 2D équivalente
   * (`kPop = P/(P−z)`, recentrage vers l'axe caméra compris) : même image,
   * mais rastérisée à la taille finale. Les profondeurs négatives restent
   * en vrai Z — rapetisser un raster est toujours net. Le moteur GL hérite
   * de cette cuisson telle quelle : c'est elle qui définit la pose réglée
   * par Vincent, pas une limite de Safari qu'on pourrait « corriger ».
   */
  const zPop = Math.max(z, 0);
  const kPop = WHEEL.perspective / (WHEEL.perspective - zPop);
  const z3d = Math.min(z, 0);
  const fade = p > 1 ? Math.max(0, 1 - (p - 1) / fadeSpan) : 1;

  /**
   * Culling par l'écran : sortie fondue au-delà du trajet, ou carte entière
   * sous le bord bas. On ne cull que la pile au repos : pendant une
   * ouverture, les écartées s'animent.
   */
  const beyondTop = p > 1 + fadeSpan + 0.05;
  const belowScreen = yCenter - cardH / 2 > H + 40;

  return {
    index,
    p,
    yCenter,
    /**
     * `yCenter` est un objectif VIEWPORT ; `− stageTop` le reconvertit en
     * repère de scène. La scène est ainsi collée au viewport quel que soit
     * l'état de son rail sticky — avant l'épinglage, épinglée, ou poussée en
     * bout de rail : aucun de ces régimes n'est plus visible. (`− cardH/2` :
     * c'est le CENTRE de la carte qui suit le trajet.)
     */
    y: WHEEL.originY + (yCenter - WHEEL.originY) * kPop - (cardH * kPop) / 2 - stageTop,
    z3d,
    tilt,
    kPop,
    fade,
    visible: !(beyondTop || belowScreen),
  };
}

/**
 * L'avancement de l'apparition d'une carte : 0 = pas encore là, 1 = en place.
 *
 * `age` est le temps écoulé depuis le montage de la scène, en secondes. Le
 * décalage temporel n'est PAS indexé sur le rang dans la liste mais sur la
 * position de la carte SUR LE TRAJET, en pas de défilement au-dessus de la
 * lecture : la carte de lecture (et toutes celles qui attendent sous elle)
 * part en premier, et chaque cran vers la sortie coûte un `stagger`. C'est ce
 * qui rend l'apparition indépendante du nombre de cartes — à 8 comme à 30,
 * elle dure le temps de traverser l'écran, pas le temps de compter la liste.
 */
export function entranceAt(ctx: StackContext, index: number, age: number): number {
  if (ENTRANCE.duration <= 0) return 1;
  const rank = Math.max(0, (ctx.advance + ctx.base) / Math.max(ctx.stepPx, 1) - index);
  const t = (age - rank * ENTRANCE.stagger) / ENTRANCE.duration;
  return ENTRANCE_EASE(Math.min(Math.max(t, 0), 1));
}

/** Toutes les poses d'une frame — l'entrée du moteur GL, qui rend d'un bloc. */
export function computeStackFrame(input: StackFrameInput): StackFrame {
  const ctx = stackContext(input);
  const poses: CardPose[] = [];
  for (let index = 0; index < input.count; index++) poses.push(poseAt(ctx, index));
  return { poses, cardH: ctx.cardH, scale: ctx.scale };
}

/**
 * Le grossissement caméra maximal du trajet — celui de la carte la plus
 * poppée. C'est le budget de largeur : la carte de lecture doit atteindre
 * exactement la largeur disponible, jamais plus (full-bleed).
 */
export function maxPop(): number {
  return (
    WHEEL.perspective /
    (WHEEL.perspective - Math.max(0, WHEEL.bottomZ, WHEEL.midZ, WHEEL.topZ))
  );
}

/**
 * La largeur VISUELLE maximale d'une carte : la colonne téléphone (§D35),
 * moins les gouttières. C'est la largeur qu'atteint la carte de lecture — au
 * pop maximal du trajet — et, depuis §D45, la carte ouverte.
 *
 * Le plafond de colonne s'applique d'abord, la gouttière ensuite : sur un
 * grand écran, l'air se prend DANS la colonne de 430, pas autour d'elle.
 */
export function stackColumnWidth(measuredWidth: number): number {
  return Math.max(MIN_STACK_W, Math.min(measuredWidth, MAX_STACK_W) - 2 * WHEEL.gutter);
}

/**
 * La largeur à donner aux cartes, depuis la largeur mesurée de la scène.
 *
 * La division par `maxPop()` BUDGÈTE le grossissement caméra : sans elle, la
 * carte de lecture débordait de l'écran d'un téléphone (×1,16 sur une carte
 * déjà pleine largeur), tranchée des deux bords. C'est une largeur de BASE :
 * aucune carte ne s'affiche à cette taille-là, elles sont toutes grossies du
 * pop de leur pose, et la plus poppée retombe exactement sur la colonne
 * utile.
 */
export function budgetedCardWidth(measuredWidth: number): number {
  return stackColumnWidth(measuredWidth) / maxPop();
}

/**
 * La course de défilement : un écran, plus le trajet que les cartes
 * excédentaires doivent parcourir. La scène étant épinglée au viewport, cette
 * hauteur ne « contient » rien — elle ne fait que dimensionner le scroll.
 *
 * Attention, ce n'est PAS un appel à `computeStackFrame` : le conteneur ne
 * connaît pas la largeur budgétée au moment où il se dimensionne, et se
 * contente d'une approximation de la hauteur de carte (colonne utile × ratio
 * d'écran). L'approximation suffit — l'excès de course est absorbé par la
 * borne sur l'avancement, qui arrête la pile net sur la dernière carte. La
 * reproduire à l'identique est indispensable : une course différente
 * déplacerait toutes les butées.
 *
 * **La gouttière entre dans l'approximation, délibérément** (§D45). Elle
 * rétrécit la carte pour de vrai : l'ignorer ici ferait DÉRIVER l'écart avec
 * la hauteur réelle à chaque cran du curseur (×1,23 à 12 px, ×1,43 à 40),
 * alors qu'en l'incluant l'écart reste exactement `maxPop`, quelle que soit
 * la gouttière. L'invariant qui compte est préservé dans les deux cas — la
 * course reste toujours ≥ l'avancement maximal, puisque l'approximation
 * SURESTIME encore la hauteur de carte du facteur `maxPop`. À gouttière 0,
 * l'expression est celle d'avant, au caractère près.
 */
export function collapsedStackHeight(count: number, H: number, W: number): number {
  const readY = H - WHEEL.frontGap;
  const topYpx = WHEEL.topY * H;
  const span = Math.max(120, readY - topYpx);
  const cardH = stackColumnWidth(W) * (H / W);
  const yTarget = WHEEL.startY * H + cardH / 2;
  const easedCap = Math.min(Math.max((readY - yTarget) / span, 0), 1);
  const pCap = gapInverse(easedCap);
  const easedEnd = Math.min(Math.max((readY - WHEEL.endY * H) / span, 0), 1);
  const pEnd = gapInverse(easedEnd);
  const base = Math.min(Math.max(0, count - 1) * WHEEL.step, pCap * span);
  const travel = Math.max(0, Math.max(0, count - 1) * WHEEL.step - base + pEnd * span);
  return Math.ceil(H + travel);
}

// ─────────────────────────────────────────────────────────────────────────
// La mesure du défilement
// ─────────────────────────────────────────────────────────────────────────

/**
 * Où tombe le centre du viewport dans le repère de la pile — l'axe de la
 * caméra — plus ce qu'il faut pour poser une frame.
 */
export interface SceneMeasure {
  focus: number;
  width: number;
  speed: number;
  /**
   * Position viewport du bord haut de la SCÈNE. Indispensable pour centrer
   * la carte ouverte : la scène sticky n'est pas toujours collée au haut du
   * viewport — en fin de rail elle se fait pousser au-dessus — et une pose
   * exprimée dans son repère serait décalée d'autant à l'écran.
   */
  stageTop: number;
}

/** Ce que le tracker va chercher dans le DOM à chaque frame. */
export type SceneReader = () => { focus: number; width: number; stageTop: number } | null;

export interface ScrollTracker {
  start(): void;
  stop(): void;
}

/**
 * Le suivi du défilement, hors React.
 *
 * La mesure passe par un `requestAnimationFrame` (l'événement part bien plus
 * souvent qu'on ne repeint) et l'écoute est en capture sur `window`, parce
 * que les événements de défilement ne remontent pas et qu'on ne veut pas
 * coder ici l'identité de l'élément qui défile.
 *
 * Extrait de `useViewportFocus` pour que le moteur GL réagisse à la vitesse
 * **exactement** comme le DOM : `speedDepth` est un effet visible, et deux
 * lissages légèrement différents se verraient au A/B des pichenettes.
 */
export function createScrollTracker(read: SceneReader, emit: (m: SceneMeasure) => void): ScrollTracker {
  let frame = 0;
  /**
   * Vitesse de défilement lissée (px/s), pour les effets qui réagissent au
   * geste (`speedDepth`). Lissée en mesure — la dérivée brute par frame est
   * trop nerveuse — et ramenée à zéro par une petite boucle de décroissance
   * quand les événements s'arrêtent : sans elle, la dernière vitesse
   * resterait figée et l'effet avec.
   */
  const last = { f: 0, t: 0, v: 0, decay: 0 };
  let latest: SceneMeasure | null = null;

  const measure = () => {
    frame = 0;
    const read0 = read();
    if (!read0) return;
    const now = performance.now();
    if (last.t > 0) {
      const inst = ((read0.focus - last.f) / Math.max(1, now - last.t)) * 1000;
      last.v = last.v * 0.7 + inst * 0.3;
    }
    last.f = read0.focus;
    last.t = now;
    latest = { focus: read0.focus, width: read0.width, speed: last.v, stageTop: read0.stageTop };
    emit(latest);
    startDecay();
  };

  const startDecay = () => {
    if (last.decay) return;
    const tick = () => {
      last.decay = requestAnimationFrame(() => {
        if (performance.now() - last.t > 80) {
          last.v *= 0.8;
          if (Math.abs(last.v) < 5) last.v = 0;
          if (latest) {
            latest = { ...latest, speed: last.v };
            emit(latest);
          }
        }
        if (last.v !== 0) tick();
        else last.decay = 0;
      });
    };
    tick();
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(measure);
  };

  return {
    start() {
      measure();
      window.addEventListener("scroll", schedule, true);
      window.addEventListener("resize", schedule);
    },
    stop() {
      if (frame) cancelAnimationFrame(frame);
      if (last.decay) cancelAnimationFrame(last.decay);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
    },
  };
}
